<template :id="root">
  <button
    v-if="!skeleton"
    :class="`${root} ${root}__${color} __${variant} __${size} __${
      block ? 'block' : ''
    } ${curved ? 'curved' : 'default-radius'} ${loader ? 'loading' : ''}`"
    :disabled="isDisabled"
    @click="$emit('buttonClicked', true)"
  >
    {{ !loader ? text : '' }}
    <div class="spinner" v-if="loader && !isDisabled">
      <div class="bounce1"></div>
      <div class="bounce2"></div>
      <div class="bounce3"></div>
    </div>
  </button>
  <div
    v-else
    :class="`${root} ${root}__skeleton ${
      block ? `${root}__block` : ''
    } ${root}__${size} ${curved ? 'curved' : 'default-radius'}`"
  ></div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';

export default defineComponent({
  name: 'Button',
  props: {
    text: {
      type: String,
      required: true,
    },
    color: {
      type: String,
      default: 'purple',
      validator: (val: string) =>
        [
          'purple',
          'yellow',
          'black',
          'success',
          'danger',
          'primary',
          'secondary',
          'dangernew',
          'secondary-grey',
          'ghost',
        ].includes(val),
    },
    size: {
      type: String,
      default: 'large',
      validator: (val: string) => ['small', 'medium', 'large'].includes(val),
    },
    variant: {
      type: String,
      default: 'fill',
      required: false,
      validator: (val: string) => ['fill', 'outline'].includes(val),
    },
    block: {
      type: Boolean,
      default: true,
    },
    loader: {
      type: Boolean,
      default: false,
    },
    disabled: Boolean,
    curved: {
      type: Boolean,
      default: false,
    },
    skeleton: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const isDisabled = computed(() => props.disabled);
    return {
      isDisabled,
    };
  },
  data: () => ({
    root: 'yc--button',
  }),
});
</script>

<style lang="scss">
@use '../../assets/scss/colors' as colors;
@use '../../assets/scss/include-media' as media;

.yc--button {
  font-family: Jost, sans-serif;
  font-style: normal;
  text-align: center;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  width: auto;
  margin: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  &.default-radius {
    border-radius: 10px;
  }

  &.curved {
    border-radius: 32px;
  }

  &.__block {
    width: 100%;
  }

  &.__large {
    font-size: 16px;
    font-weight: 500;
    height: 48px;
    padding: 12px 16px;
    gap: 10px;

    &:not(.__block) {
      width: 328px;
    }
  }

  &.__large.__block {
    width: 100%;
  }

  &.__medium {
    font-size: 14px;
    font-weight: 500;
    height: 40px;
    padding: 0 16px;
  }

  &.__small {
    font-size: 12px;
    font-weight: 400;
    height: 37px;
    padding: 8px;
    gap: 10px;

    &:not(.__block) {
      width: 136px;
    }
  }

  &.__small.__block {
    width: 100%;
  }

  &__yellow {
    color: colors.$brand-black !important;
    border: 1px solid colors.$brand-primary-yellow !important;
    background: colors.$brand-primary-yellow !important;

    &.__outline {
      color: colors.$brand-primary-yellow-500 !important;
      border: 1px solid colors.$brand-primary-yellow-500 !important;
      background: #fff !important;
    }

    &:hover {
      color: colors.$brand-black !important;
      border: 1px solid colors.$brand-primary-yellow-500 !important;
      background: colors.$brand-primary-yellow-500 !important;
    }
  }

  &__purple {
    color: #fff !important;
    border: 1px solid colors.$brand-secondary-purple !important;
    background: colors.$brand-secondary-purple !important;

    &.__outline {
      color: colors.$brand-secondary-purple !important;
      border: 1px solid colors.$brand-secondary-purple !important;
      background: #fff !important;
    }

    &:hover {
      color: #fff !important;
      border: 1px solid colors.$brand-secondary-purple-600 !important;
      background: colors.$brand-secondary-purple-600 !important;
    }
  }

  &__black {
    border: 1px solid colors.$brand-black !important;
    background: colors.$brand-black !important;

    &.__outline {
      color: colors.$brand-black !important;
      border: 1px solid colors.$brand-black !important;
      background: #fff !important;
    }

    &:hover {
      color: #fff !important;
      border: 1px solid colors.$brand-black !important;
      background: colors.$brand-black !important;
    }
  }

  &__success {
    color: #fff !important;
    border: 1px solid colors.$status-success !important;
    background: colors.$status-success !important;

    &.__outline {
      color: colors.$status-success !important;
      border: 1px solid colors.$status-success !important;
      background: #fff !important;
    }

    &:hover {
      color: #fff !important;
      border: 1px solid colors.$status-success !important;
      background: colors.$status-success !important;
    }
  }

  &__danger {
    color: #fff !important;
    border: 1px solid colors.$status-error !important;
    background: colors.$status-error !important;

    &.__outline {
      color: colors.$status-error !important;
      border: 1px solid colors.$status-error !important;
      background: #fff !important;
    }

    &:hover {
      color: #fff !important;
      border: 1px solid colors.$status-error !important;
      background: colors.$status-error !important;
    }
  }

  &:disabled {
    color: #bdbdbd !important;
    background: #f5f5f5 !important;
    cursor: not-allowed !important;
  }

  &__primary {
    color: #fff !important;
    border: 1px solid #492b7c !important;
    background: #492b7c !important;

    &.__outline {
      color: #492b7c !important;
      border: 1px solid #492b7c !important;
      background: #fff !important;
    }

    &:hover {
      color: #fff !important;
      border: 1px solid #674e92 !important;
      background: #674e92 !important;
    }

    &:focus {
      color: #fff !important;
      border: 1px solid #8672a8 !important;
      background: #8672a8 !important;
    }

    &:active {
      color: #fff !important;
      border: 1px solid #a495bd !important;
      background: #a495bd !important;
    }

    &.loading {
      color: #fff !important;
      border: 1px solid #3d2467 !important;
      background: #3d2467 !important;
    }

    &:disabled {
      border: 1px solid #ffffff !important;
    }
  }

  &__secondary {
    color: #492b7c !important;
    border: 1px solid #492b7c !important;
    background: #fff !important;

    &.__outline {
      color: #492b7c !important;
      border: 1px solid #492b7c !important;
      background: #fff !important;
    }

    &:hover {
      color: #fff !important;
      border: 1px solid #674e92 !important;
      background: #674e92 !important;
    }

    &:focus {
      color: #fff !important;
      border: 1px solid #8672a8 !important;
      background: #8672a8 !important;
    }

    &:active {
      color: #fff !important;
      border: 1px solid #a495bd !important;
      background: #a495bd !important;
    }

    &.loading {
      color: #fff !important;
      border: 1px solid #3d2467 !important;
      background: #3d2467 !important;
    }

    &:disabled {
      border: 1px solid #f5f5f5 !important;
    }
  }

  &__dangernew {
    color: #ffffff !important;
    border: 1px solid colors.$status-error !important;
    background: colors.$status-error !important;

    &.__outline {
      color: colors.$status-error !important;
      border: 1px solid colors.$status-error !important;
      background: #fff !important;
    }

    &:hover {
      color: #ffffff !important;
      border: 1px solid #bf1634 !important;
      background: #bf1634 !important;
    }

    &:focus {
      color: #ffffff !important;
      border: 1px solid #db2d4c !important;
      background: #db2d4c !important;
    }

    &:active {
      color: #ffffff !important;
      border: 1px solid #9e001d !important;
      background: #9e001d !important;
    }

    &.loading {
      color: #ffffff !important;
      border: 1px solid #9e001d !important;
      background: #9e001d !important;
    }

    &:disabled {
      border: 1px solid #f5f5f5 !important;
    }
  }

  &__secondary-grey {
    color: #000000 !important;
    border: 1px solid #eeeeee !important;
    background: #ffffff !important;

    &.__outline {
      color: #000000 !important;
      border: 1px solid #eeeeee !important;
      background: #fff !important;
    }

    &:hover {
      color: #000000 !important;
      border: 1px solid #e0e0e0 !important;
      background: #e0e0e0 !important;
    }

    &:focus {
      color: #000000 !important;
      border: 1px solid #eeeeee !important;
      background: #eeeeee !important;
    }

    &:active {
      color: #000000 !important;
      border: 1px solid #f5f5f5 !important;
      background: #f5f5f5 !important;
    }

    &.loading {
      color: #000000 !important;
      border: 1px solid #9e9e9e !important;
      background: #9e9e9e !important;
    }

    &:disabled {
      border: 1px solid #f5f5f5 !important;
    }
  }

  &__ghost {
    color: #492b7c !important;
    border: 1px solid #ffffff !important;
    background: #ffffff !important;

    &.__outline {
      color: #492b7c !important;
      border: 1px solid #ffffff !important;
      background: #ffffff !important;
    }

    &:hover {
      color: #674e92 !important;
      border: 1px solid #ffffff !important;
      background: #ffffff !important;
    }

    &:focus {
      color: #8672a8 !important;
      border: 1px solid #ffffff !important;
      background: #ffffff !important;
    }

    &:active {
      color: #a495bd !important;
      border: 1px solid #ffffff !important;
      background: #ffffff !important;
    }

    &.loading {
      color: #fff !important;
      border: 1px solid #3d2467 !important;
      background: #3d2467 !important;
    }

    &:disabled {
      color: #ffffff !important;
      background: #ffffff !important;
      border: 1px solid #ffffff !important;
      cursor: not-allowed !important;
    }
  }

  &__skeleton {
    background: #eeeeee !important;
    height: 56px; /* Default to large height */
    padding: 0 20px;
    border: none;

    &.__small {
      height: 26px;
      padding: 0 13px;
    }

    &.__medium {
      height: 40px;
      padding: 0 16px;
    }
  }

  .loader {
    border: 3px solid #cccccc;
    border-top: 3px solid colors.$brand-secondary-purple;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    animation: spin 2s linear infinite;
    justify-content: center;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .spinner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 56px;
  }

  .spinner > div {
    width: 12px;
    height: 12px;
    background-color: #ffffff;
    border-radius: 100%;
    display: inline-block;
    animation: bouncedelay 1s infinite ease-in-out both;
  }

  .spinner .bounce1 {
    opacity: 0.8;
    animation-delay: -0.32s;
  }

  .spinner .bounce2 {
    opacity: 0.6;
    animation-delay: -0.16s;
  }

  .spinner .bounce3 {
    opacity: 0.3;
    animation-delay: -0.16s;
  }

  @keyframes bouncedelay {
    0%,
    80%,
    100% {
      transform: scale(0);
    }
    40% {
      transform: scale(1);
    }
  }
}
</style>
